import * as Sentry from '@sentry/nextjs';
import {
  CustomFormApi,
  Form,
  Multistep,
  useFormState,
  useMultistepState,
} from 'informed';
import { useRouter } from 'next/navigation';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { RegistrationFormProps } from '../RegistrationForm';
import {
  createErrorObjects,
  getUserInfoInitialValues,
  IError,
} from './helpers';
import { AddressInformationStep, PersonalInformationStep } from './steps';
import { AnalyticsEventName } from '@/api/analytics';
import {
  AddressInformationStepApi,
  FullInformationForm,
  PersonalInformationStepForm,
  updatePlayer,
  UpdatePlayerContext,
} from '@/api/auth';
import { replaceErrorField } from '@/components/form/utils';
import { hasShownLimitModal } from '@/components/modals/KnowYourLimit/helpers';
import {
  fieldsMap,
  LOCAL_STORAGE_USER_INFO_FORM_KEY,
  NamesSteps,
  replaceErrorFieldMap,
} from '@/helpers/auth';
import {
  dateFormat,
  getValuesFromLocalStorage,
  removeSpaces,
} from '@/helpers/common';
import { countriesWithPhoneNumber } from '@/helpers/countriesWithPhoneNumber';
import { Modals } from '@/helpers/modals';
import { showTooltip } from '@/helpers/tooltip';
import { useAnalytics } from '@/hooks/analytics';
import useEstonia from '@/hooks/estonia';
import { useNavigation } from '@/hooks/navigation';
import { useSwrMutation } from '@/hooks/swr';
import { useIsLanding } from '@/hooks/useIsLanding';
import { DepositMode } from '@/modules/deposit';
import {
  sendAdditionalUserDataStep2AppearanceEvent,
  sendAdditionalUserDataStep2FirstTouchedEvent,
  sendAdditionalUserDataStep3AppearanceEvent,
  sendAdditionalUserDataStep3FirstTouchedEvent,
  sendAdditionalUserDataStep3ResultEvent,
} from '@/modules/googleAnalytics/events/additionalDataEvents';
import {
  STORAGE,
  useCommonStore,
  useDepositStore,
  useLocalStorageStore,
  useModalsStore,
} from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';
import { TooltipTypes } from '@/store/tooltipStore';

export const FullUserInformationForm: FC<RegistrationFormProps> = (props) => {
  const { t } = useTranslation('common');

  const { sendEventToAnalytics } = useAnalytics();
  const { isEstonia } = useEstonia();
  const limitModalShown = hasShownLimitModal();
  const { navigate } = useNavigation();
  const { isLanding } = useIsLanding();

  const open = useModalsStore.use.open();
  const close = useModalsStore.use.close();

  const mode = useDepositStore.use.mode();

  const {
    user,
    pendingUser,
    fetchUser,
    afterLoginRedirectUrl,
    afterAdditionalDataRedirectUrl,
  } = useCommonStore(
    useShallow((state) => ({
      user: state.user,
      pendingUser: state.pendingUser,
      fetchUser: state.fetchUser,
      afterLoginRedirectUrl: state.afterLoginRedirectUrl,
      afterAdditionalDataRedirectUrl: state.afterAdditionalDataRedirectUrl,
    }))
  );

  const {
    trigger: requestUpdatePlayer,
    data: userData,
    error: updatePlayerErrors,
    isMutating: pendingUpdatePlayer,
  } = useSwrMutation({
    key: 'updatePlayer',
    fetcher: updatePlayer,
    options: { populateCache: true },
  });

  const formApiRef = useRef<CustomFormApi<FullInformationForm>>();
  const { push } = useRouter();

  const handleSubmit = useCallback(
    async ({
      ...values
    }: PersonalInformationStepForm & AddressInformationStepApi) => {
      let requestData;
      try {
        sendEventToAnalytics({
          name: AnalyticsEventName.MODAL__ADDITIONAL_USER_DATA__FORM__SUBMIT,
        });

        const mobileCode = countriesWithPhoneNumber.find(
          ({ iso2 }) => iso2 === values.mobilePhone.code
        );

        if (mobileCode == null) return;

        const userInfoFromStorage: undefined | Partial<FullInformationForm> =
          getValuesFromLocalStorage(LOCAL_STORAGE_USER_INFO_FORM_KEY);

        if (userInfoFromStorage?.step3) delete userInfoFromStorage.step3.state;

        requestData = {
          context: UpdatePlayerContext.Edition,
          player: {
            ...userInfoFromStorage?.step2,
            ...userInfoFromStorage?.step3,
            ...values,
            dateOfBirth: dateFormat(
              `${values.dateOfBirth.day}/${values.dateOfBirth.month}/${values.dateOfBirth.year}`
            ),
            mobilePhone: removeSpaces(
              `+${mobileCode.dialCode}${values.mobilePhone.number}`
            ),
          },
        };
        const updatedPlayer = await requestUpdatePlayer(requestData);

        if (updatedPlayer) {
          await fetchUser(REASON_FOR_UPDATING_USER.REGISTRATION);

          sendEventToAnalytics({
            name: AnalyticsEventName.MODAL__ADDITIONAL_USER_DATA__FORM__SUCCESS,
          });

          sendAdditionalUserDataStep3ResultEvent({
            status: 'success',
          });

          showTooltip({
            type: TooltipTypes.SUCCESS,
            body: t('auth.registration.userData.tooltips.success'),
          });

          localStorage.removeItem(LOCAL_STORAGE_USER_INFO_FORM_KEY);

          if (afterLoginRedirectUrl) {
            close();
            push(afterLoginRedirectUrl);
            return;
          }

          if (isEstonia && !limitModalShown) {
            useCommonStore.setState({
              isKnowYourLimit: true,
              afterRegStep: true,
            });
            useLocalStorageStore
              .getState()
              .replace(STORAGE.HAS_SHOWN_LIMIT_MODAL, true);
            close();
            return;
          }

          if (isLanding) {
            useCommonStore.setState({
              regBanner: null,
              initialRegFormCurrency: null,
            });
            if (afterAdditionalDataRedirectUrl) {
              navigate(afterAdditionalDataRedirectUrl);
            } else {
              navigate('/?modal=deposit');
            }

            return;
          }

          if (mode === DepositMode.SPORTS) return;

          open(Modals.Deposit);
        }
      } catch (error) {
        sendAdditionalUserDataStep3ResultEvent({
          status: 'failed',
          error_data: error,
          request_data: requestData,
        });
        Sentry.captureMessage(
          `Error in full information request - ${JSON.stringify(error)}`
        );
      }
    },
    [
      afterAdditionalDataRedirectUrl,
      sendEventToAnalytics,
      requestUpdatePlayer,
      fetchUser,
      t,
      afterLoginRedirectUrl,
      close,
      push,
      isEstonia,
      limitModalShown,
      open,
      isLanding,
      navigate,
      mode,
    ]
  );

  const errors = useMemo(() => {
    if (updatePlayerErrors == null) return [];

    let accError = createErrorObjects(updatePlayerErrors);

    if (updatePlayerErrors.profile != null) {
      accError = [
        ...accError,
        ...createErrorObjects(updatePlayerErrors.profile),
      ];
    }

    return replaceErrorField(accError, replaceErrorFieldMap);
  }, [updatePlayerErrors]);

  const step = useMemo(() => {
    if (userData != null) {
      return NamesSteps.ADDRESS_INFORMATION;
    }
    const steps = Object.keys(fieldsMap);

    if (errors.length === 0) {
      return NamesSteps.PERSONAL_INFORMATION;
    }

    const errorFields = errors.map(({ field }) => field);

    const step =
      steps.find((step) =>
        fieldsMap[step].find((field) => errorFields.includes(field))
      ) || NamesSteps.PERSONAL_INFORMATION;

    return step;
  }, [errors, userData]);

  useEffect(() => {
    const formRef = formApiRef.current;

    if (formRef == null || errors.length === 0) return;

    errors.forEach(({ field, message }) => {
      formRef?.setError(field, message);
    });
  }, [errors]);

  useEffect(() => {
    if (updatePlayerErrors) {
      showTooltip({
        type: TooltipTypes.ERROR,
        body: t('auth.registration.userData.tooltips.failure'),
      });
    }
  }, [updatePlayerErrors, t]);

  useEffect(() => {
    const formRef = formApiRef.current;

    return () => {
      if (formRef == null) return;

      try {
        const savedValues: FullInformationForm =
          getValuesFromLocalStorage(LOCAL_STORAGE_USER_INFO_FORM_KEY) || {};
        const { values } = formRef.getFormState();

        localStorage.setItem(
          LOCAL_STORAGE_USER_INFO_FORM_KEY,
          JSON.stringify({ ...savedValues, ...values })
        );
      } catch (error) {
        console.log(error);
      }
    };
  }, []);

  const initialValues = useMemo(() => getUserInfoInitialValues(user), [user]);

  return (
    <Form<FullInformationForm>
      formApiRef={formApiRef}
      onSubmit={({ values: { step2, step3 } }) => {
        return handleSubmit({ ...step2, ...step3 });
      }}
      initialValues={initialValues}
    >
      <Analytics />
      <Multistep initialStep={step}>
        <Steps
          {...props}
          errors={errors}
          isLoading={pendingUpdatePlayer || pendingUser}
        />
      </Multistep>
    </Form>
  );
};

interface StepsProps extends RegistrationFormProps {
  isLoading: boolean;
  errors: IError[];
}

const Steps: FC<StepsProps> = ({ errors, ...props }) => {
  const { sendEventToAnalytics } = useAnalytics();
  const { current } = useMultistepState();
  const formState = useFormState();

  const formTouched = useRef({ step2: false, step3: false });

  useEffect(() => {
    if (formState.touched.step2 && !formTouched.current.step2) {
      formTouched.current.step2 = true;
      sendAdditionalUserDataStep2FirstTouchedEvent();
    }
    if (formState.touched.step3 && !formTouched.current.step3) {
      formTouched.current.step3 = true;
      sendAdditionalUserDataStep3FirstTouchedEvent();
    }
  }, [formState]);

  useEffect(() => {
    useCommonStore.setState({ currentRegistrationStep: current as NamesSteps });
  }, [current]);

  useEffect(() => {
    if (!current) return;

    current === NamesSteps.PERSONAL_INFORMATION
      ? sendAdditionalUserDataStep2AppearanceEvent()
      : sendAdditionalUserDataStep3AppearanceEvent();

    // TODO: remove after GA4 migration
    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__ADDITIONAL_USER_DATA__STEP__APPEARANCE,
      dynamicParams: { step: current },
    });
  }, [sendEventToAnalytics, current]);

  return (
    <>
      <PersonalInformationStep
        step={NamesSteps.PERSONAL_INFORMATION}
        errors={errors}
      />
      <AddressInformationStep
        step={NamesSteps.ADDRESS_INFORMATION}
        errors={errors}
        {...props}
      />
    </>
  );
};

const Analytics = () => {
  const { sendEventToAnalytics } = useAnalytics();

  const { touched } = useFormState();
  const touchedForm = Object.values(touched).some((t) => t);

  useEffect(() => {
    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__ADDITIONAL_USER_DATA__FORM__APPEARANCE,
    });
  }, [sendEventToAnalytics]);

  useEffect(() => {
    if (touchedForm) {
      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__ADDITIONAL_USER_DATA__FORM__TOUCHED,
      });
    }
  }, [touchedForm, sendEventToAnalytics]);

  return null;
};
